import AdminUser from '../../models/admin/AdminUser';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { ContentReviewInterface } from '../../models/ContentReviewInterface';
import { ReviewRequest, ReviewStatus } from '../../models/ReviewRequest';

export type ReviewContentType = 'FundUpdate' | 'FundOpportunity';
export type ReviewComparisonMode = 'published-vs-draft' | 'draft-vs-proposed';

export function getContentTypeFromReviewType(
  reviewType?: ReviewRequest['type'],
): ReviewContentType {
  return reviewType === 'FUND_UPDATE_REVIEW' ? 'FundUpdate' : 'FundOpportunity';
}

export function getDefaultComparisonMode(
  reviewRequest: ReviewRequest | null,
  currentUser: AdminUser | null,
): ReviewComparisonMode {
  if (!reviewRequest || !currentUser) {
    return 'published-vs-draft';
  }

  if (
    reviewRequest.requestedBy.id === currentUser.id &&
    reviewRequest.status === 'CHANGES_REQUESTED'
  ) {
    return 'draft-vs-proposed';
  }

  return 'published-vs-draft';
}

export function areAcceptedChangesAlreadySaved(
  reviewData: ContentReviewInterface | null,
  draftContent: ContentManagementInterface | null,
  hasAcceptedChanges: boolean,
): boolean {
  if (!reviewData || !draftContent || !hasAcceptedChanges) {
    return true;
  }

  return reviewData.changes
    .filter((change) => change.accepted)
    .every((change) => {
      const sectionIndex = parseInt(change.sectionId.replace('section-', ''), 10);
      if (sectionIndex >= 0 && sectionIndex < draftContent.content.length) {
        return (
          JSON.stringify(draftContent.content[sectionIndex]) === JSON.stringify(change.contentAfter)
        );
      }
      return false;
    });
}

export function shouldShowSaveAcceptedChangesButton(
  reviewRequest: ReviewRequest | null,
  currentUser: AdminUser | null,
  reviewData: ContentReviewInterface | null,
  draftContent: ContentManagementInterface | null,
): boolean {
  const hasAcceptedChanges = reviewData?.changes.some((change) => change.accepted) || false;
  const hasProposedChanges = (reviewData?.changes?.length || 0) > 0;
  const hasValidChanges = hasAcceptedChanges || !hasProposedChanges;
  const changesNotAlreadySaved = hasAcceptedChanges
    ? !areAcceptedChangesAlreadySaved(reviewData, draftContent, hasAcceptedChanges)
    : true;
  const isInChangesRequestedMode = reviewRequest?.status === 'CHANGES_REQUESTED';
  const isRequester =
    currentUser && reviewRequest && currentUser.id === reviewRequest.requestedBy.id;

  return Boolean(
    hasValidChanges && changesNotAlreadySaved && isInChangesRequestedMode && isRequester,
  );
}

export function canRequesterAddContent(
  reviewRequest: ReviewRequest | null,
  currentUser: AdminUser | null,
): boolean {
  if (!reviewRequest || !currentUser) {
    return false;
  }

  return (
    reviewRequest.requestedBy.id === currentUser.id && reviewRequest.status === 'CHANGES_REQUESTED'
  );
}

export function applyAcceptedChangesToDraft(
  reviewData: ContentReviewInterface,
  draftContent: ContentManagementInterface,
): ContentManagementInterface {
  return {
    ...draftContent,
    content: draftContent.content.map((item, index) => {
      const sectionId = `section-${index}`;
      const acceptedChange = reviewData.changes.find(
        (change) => change.sectionId === sectionId && change.accepted,
      );
      return acceptedChange ? acceptedChange.contentAfter : item;
    }),
    published: false,
  };
}

export function getReviewStatusColor(
  status: ReviewStatus,
): 'warning' | 'error' | 'success' | 'default' {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'CHANGES_REQUESTED':
    case 'REVIEW_REJECTED':
      return 'error';
    case 'REVIEW_COMPLETE':
      return 'success';
    default:
      return 'default';
  }
}
